import {fromEvent, merge, Observable} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import scrollToElement from 'scroll-to-element';

/**
 * Makes a copy of computed styles from one element to another.
 * If properties list is passed, it makes a copy only of specified properties. Otherwise it makes a full copy.
 *
 * @param fromElement
 * @param toElement
 * @param [properties]
 */
export function copyStyle(fromElement: HTMLElement, toElement: HTMLElement, properties?: Iterable<string>): void {
    const display = toElement.style.display;
    toElement.style.display = 'none';
    let sourceStyles = getComputedStyle(fromElement);
    let targetStyles = toElement.style;
    let propertiesList: Iterable<string> = properties ? properties : getCollectionIterator(sourceStyles);
    for (let property of propertiesList) {
        targetStyles.setProperty(property, sourceStyles.getPropertyValue(property));
    }
    toElement.style.display = display;
}

/**
 * Sets inline styles to element.
 *
 * @param element
 * @param style
 */
export function setStyle(element: HTMLElement, style: {[key: string]: string}): void {
    for (let [property, value] of Object.entries(style)) {
        element.style.setProperty(property, value);
    }
}

/**
 * Creates IterableIterator for DOM collections.
 *
 * @param collection
 * @return
 */
export function* getCollectionIterator<T = string>(collection: {length: number; item(index: number): T}): IterableIterator<T> {
    for (let i = 0, l = collection.length; i < l; i++) {
        yield collection.item(i);
    }
}

/**
 * Returns parent node, that satisfies the provided testing function. Otherwise returns null.
 *
 * @param node
 * @param checkFn
 * @return
 */
export function findParent(node: Node, checkFn: (node: HTMLElement) => boolean): HTMLElement {
    let parent = node.parentNode;
    while (parent !== document) {
        if (checkFn(parent as HTMLElement)) {
            return parent as HTMLElement;
        }
        parent = parent.parentNode;
    }
    return null;
}

/**
 * Returns sibling element, that satisfies the provided testing function. Otherwise returns null.
 *
 * @param node
 * @param direction    May be 'after' or 'before'
 * @param checkFn
 * @return
 */
export function findSibling(node: Node, direction: 'after' | 'before', checkFn: (node: HTMLElement) => boolean): HTMLElement {
    let siblingProperty: keyof HTMLElement = direction === 'before' ? 'previousElementSibling' : 'nextElementSibling';
    let sibling = node[siblingProperty] as HTMLElement;
    while (sibling instanceof HTMLElement) {
        if (checkFn(sibling)) {
            return sibling;
        }
        sibling = sibling[siblingProperty] as HTMLElement;
    }
    return null;
}

/**
 * Returns a promise, that will be resolved after "next frame". Uses requestAnimationFrame or setTimeout, depends on passed time.
 * If time is specified, it uses setTimeout, otherwise uses requestAnimationFrame
 *
 * @param time     milliseconds
 * @return
 */
export function nextFrame(time?: number): Promise<void> {
    return new Promise(resolve => {
        if (isFinite(time) && time >= 0) {
            setTimeout(() => resolve(), time);
        } else {
            requestAnimationFrame(() => resolve());
        }
    });
}

/**
 * Returns a promise, that will be resolved after 'transitionend' event for one of specified properties.
 *
 * @param element
 * @param propertyName
 * @param [timeout]
 * @return
 */
export function transitionEnd(element: Element, propertyName: Array<string>, timeout?: number): Promise<Element> {
    return new Promise(resolve => {
        let checker = new RegExp(propertyName.join('|'));
        let timeoutId;
        let listener = (event: TransitionEvent) => {
            if (event.target === element && checker.test(event.propertyName)) {
                clearTimeout(timeoutId);
                element.removeEventListener('transitionend', listener);
                resolve(element);
            }
        };
        element.addEventListener('transitionend', listener);
        if (isFinite(timeout) && timeout > 0) {
            timeoutId = setTimeout(() => {
                element.removeEventListener('transitionend', listener);
                resolve(element);
            }, timeout);
        }
    });
}

/**
 * Returns a promise, that will be resolved after 'animationend' event for one of specified animations.
 *
 * @param element
 * @param animationName
 * @param [timeout]
 * @return
 */
export function animationEnd(element: Element, animationName: Array<string>, timeout?: number): Promise<Element> {
    return new Promise(resolve => {
        let checker = new RegExp(animationName.join('|'));
        let timeoutId;
        let listener = (event: AnimationEvent) => {
            if (event.target === element && checker.test(event.animationName)) {
                clearTimeout(timeoutId);
                element.removeEventListener('animationend', listener);
                resolve(element);
            }
        };
        element.addEventListener('animationend', listener);
        if (isFinite(timeout) && timeout > 0) {
            timeoutId = setTimeout(() => {
                element.removeEventListener('animationend', listener);
                resolve(element);
            }, timeout);
        }
    });
}

/**
 * Creates mutation observer, and returns an Observable for it.
 *
 * @param target
 * @param init
 * @return
 */
export function observeMutations(target: Element, init: MutationObserverInit): Observable<Array<MutationRecord>> {
    return new Observable(sub => {
        let observer = new MutationObserver(records => {
            sub.next(records);
        });
        observer.observe(target, init);
        return () => observer.disconnect();
    });
}

/**
 * Creates IterableIterator for all added nodes of array of mutation records.
 *
 * @param records
 * @return
 */
export function* getAddedNodesIterator(records: Array<MutationRecord>): IterableIterator<Node> {
    for (let record of records) {
        for (let node of record.addedNodes) {
            yield node;
        }
    }
}

/**
 * Creates an observable, that emits updated MediaQueryList when media query breakpoint is fired.
 *
 * @param mediaQuery
 * @param [immediate]
 * @return
 */
export function observeMediaQuery(mediaQuery: string, immediate = false): Observable<MediaQueryListEvent> {
    return new Observable(sub => {
        let mql = window.matchMedia(mediaQuery);
        let listener = event => sub.next(event);
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', listener);
        } else {
            mql.addListener(listener);
        }

        let emitInitialValue = () => {
            if (typeof mql.dispatchEvent === 'function') {
                mql.dispatchEvent(
                    new MediaQueryListEvent('change', {
                        matches: mql.matches,
                        media: mql.media,
                    })
                );
            } else {
                listener(mql);
            }
        };
        if (immediate) {
            emitInitialValue();
        } else {
            setTimeout(emitInitialValue, 0);
        }
        return () => {
            if (typeof mql.removeEventListener === 'function') {
                mql.removeEventListener('change', listener);
            } else {
                mql.removeListener(listener);
            }
        };
    });
}

/**
 * Asynchronously selects text in element.
 *
 * @param element
 * @return
 */
export async function selectText(element: HTMLElement): Promise<Selection> {
    let selection = element.ownerDocument.getSelection();
    selection.removeAllRanges();
    let range = element.ownerDocument.createRange();
    range.selectNodeContents(element);
    selection.addRange(range);
    await nextFrame();
    return element.ownerDocument.getSelection();
}

/**
 * Returns a set of all custom properties in application
 *
 * @return
 */
export function getAllCustomProperties(): Set<string> {
    const customPropertyRegExp = /^--/;
    let properties = new Set<string>();
    for (let styleSheet of getCollectionIterator(document.styleSheets)) {
        let rules = getCssRules(styleSheet as CSSStyleSheet);
        if (rules) {
            for (let rule of getCollectionIterator(rules)) {
                if (rule instanceof CSSStyleRule) {
                    for (let property of getCollectionIterator((<CSSStyleRule>rule).style)) {
                        if (customPropertyRegExp.test(property)) {
                            properties.add(property);
                        }
                    }
                }
            }
        }
    }
    return properties;
}

function getCssRules(styleSheet: CSSStyleSheet): CSSRuleList {
    try {
        return styleSheet.cssRules || null;
    } catch (e) {
        console.log('Unable to read style rules from stylesheet:', styleSheet.href, e);
        return null;
    }
}

export function isFullscreenSupported(): boolean {
    let method = ['requestFullscreen', 'webkitRequestFullscreen', 'mozRequestFullScreen'].find(
        methodName => typeof document.body[methodName] === 'function'
    );
    return !!method;
}

export function openInFullScreen(el: HTMLElement) {
    let method = ['requestFullscreen', 'webkitRequestFullscreen', 'mozRequestFullScreen'].find(
        methodName => typeof el[methodName] === 'function'
    );
    if (method) {
        el[method]();
    }
}

export function exitFullscreen() {
    let method = ['exitFullscreen', 'webkitExitFullscreen', 'mozCancelFullScreen'].find(
        methodName => typeof document[methodName] === 'function'
    );
    if (method) {
        document[method]();
    }
}

export /* istanbul ignore next */ function getFullscreenElement(): HTMLElement {
    let property = ['fullscreenElement', 'webkitFullscreenElement', 'mozFullScreenElement'].find(propertyName => propertyName in document);
    if (property) {
        return document[property];
    }
    return null;
}

export /* istanbul ignore next */ function fullscreenChange(target: EventTarget): Observable<Event> {
    return merge<Event>(
        fromEvent(target, 'fullscreenchange'),
        fromEvent<Event>(target, 'webkitfullscreenchange'),
        fromEvent<Event>(document, 'mozfullscreenchange')
    );
}

export function getScrollTop(): number {
    return document.body.scrollTop || document.documentElement.scrollTop;
}

export function getScrollLeft(): number {
    return document.body.scrollLeft || document.documentElement.scrollLeft;
}

export function getScrollHeight(): number {
    return document.body.scrollHeight || document.documentElement.scrollHeight;
}

export function getScrollWidth(): number {
    return document.body.scrollWidth || document.documentElement.scrollWidth;
}

export const windowResize$: Observable<UIEvent> = fromEvent<UIEvent>(window, 'resize').pipe(debounceTime(100));

/**
 * return scroll bar with in px
 */
export function getScrollbarWidth() {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';

    document.body.appendChild(outer);

    const widthNoScroll = outer.offsetWidth;
    // force scrollbars
    outer.style.overflow = 'scroll';

    // add innerdiv
    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const widthWithScroll = inner.offsetWidth;

    // remove divs
    outer.parentNode.removeChild(outer);

    return widthNoScroll - widthWithScroll;
}

export function scrollTo(
    elem: Element,
    options: {
        ease?: string;
        offset?: number;
        align?: 'top' | 'middle' | 'bottom';
        duration?: number;
    }
): Promise<any> {
    // wait animation
    return new Promise<void>(resolve => {
        const tween = scrollToElement(elem, options);
        tween.on('end', function () {
            resolve();
        });
    });
}

export function addScript(src: string, callback?: () => void) {
    const s = document.createElement('script');
    s.setAttribute('src', src);
    s.onload = callback;
    document.body.appendChild(s);
}

export function injectScript(jsScript: string, target: HTMLElement) {
    const script = target.ownerDocument.createElement('script');
    script.innerHTML = jsScript;
    target.append(script);
}

export function injectNoScript(jsScript: string, target: HTMLElement) {
    const noScript = target.ownerDocument.createElement('noscript');
    noScript.innerHTML = jsScript;
    target.append(noScript);
}
